import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent
} from '@mui/material';

const HowItWorksSection = () => {
  const [activeStep, setActiveStep] = useState(-1);
  const [lineHeight, setLineHeight] = useState(0);
  const [totalLineHeight, setTotalLineHeight] = useState(0);
  const stepRefs = useRef([]);
  const timelineRef = useRef(null);

  useEffect(() => {
    const calculateLineHeight = (index) => {
      if (stepRefs.current[index] && timelineRef.current) {
        const timelineRect = timelineRef.current.getBoundingClientRect();
        const stepRect = stepRefs.current[index].getBoundingClientRect();
        // Calcular hasta el centro del círculo (top del step + 20px que es el radio del círculo)
        const relativeTop = stepRect.top - timelineRect.top + 20;
        setLineHeight(Math.max(0, relativeTop));
      }
    };

    // Calcular la altura total de la línea gris (hasta el último círculo)
    const calculateTotalHeight = () => {
      const lastStepIndex = stepRefs.current.length - 1;
      if (stepRefs.current[lastStepIndex] && timelineRef.current) {
        const timelineRect = timelineRef.current.getBoundingClientRect();
        const lastStepRect = stepRefs.current[lastStepIndex].getBoundingClientRect();
        const totalHeight = lastStepRect.top - timelineRect.top + 20;
        setTotalLineHeight(totalHeight);
      }
    };

    // Calcular altura total inicialmente y en resize
    calculateTotalHeight();

    const observers = stepRefs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveStep((prev) => Math.max(prev, index));
            calculateLineHeight(index);
          }
        },
        {
          threshold: 0.6,
          rootMargin: '-100px 0px -100px 0px'
        }
      );

      if (ref) {
        observer.observe(ref);
      }

      return observer;
    });

    // Recalcular altura en resize
    const handleResize = () => {
      calculateTotalHeight();
      if (activeStep >= 0) {
        calculateLineHeight(activeStep);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener('resize', handleResize);
    };
  }, [activeStep]);

  const steps = [
    {
      number: '01',
      title: 'Evaluación inicial',
      description: 'Revisamos tu contexto, historial, objetivos y disponibilidad real.'
    },
    {
      number: '02',
      title: 'Diseño del plan',
      description: 'Se construye tu programación inicial según tu objetivo y estilo de vida.'
    },
    {
      number: '03',
      title: 'Inicio del trabajo',
      description: 'El plan se pone en marcha y el progreso se evalúa semana a semana mediante check-ins y correcciones.'
    },
    {
      number: '04',
      title: 'Ajustes y optimización',
      description: 'El programa se modifica cuando es necesario para seguir avanzando hacia los objetivos macro y micro.'
    },
    {
      number: '05',
      title: 'Progreso continuo',
      description: 'Cada semana estás más cerca de tu objetivo, con un plan que evoluciona contigo.'
    }
  ];

  return (
    <Box 
      id="como-funciona" 
      sx={{ 
        bgcolor: 'white',
        py: { xs: 6, md: 10 }
      }}
    >
      <Container maxWidth="lg">
        {/* Layout de 2 columnas: Título a la izquierda, Timeline a la derecha */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '380px 1fr' },
            gap: { xs: 6, md: 6 },
            alignItems: 'start'
          }}
        >
          {/* Columna izquierda: Título y botones (sticky en desktop) */}
          <Box
            sx={{
              position: { md: 'sticky' },
              top: { md: '120px' },
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              CÓMO FUNCIONA
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ 
                fontWeight: 400,
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.6
              }}
            >
              Un proceso claro, medible y con seguimiento semanal.
            </Typography>
          </Box>

          {/* Columna derecha: Timeline con cards a la derecha */}
          <Box ref={timelineRef} sx={{ position: 'relative' }}>
            {/* Línea gris de fondo (recorrido completo) */}
            <Box
              sx={{
                position: 'absolute',
                left: '20px',
                top: '20px',
                width: '2px',
                height: totalLineHeight > 0 ? `${totalLineHeight}px` : '100%',
                bgcolor: 'grey.300',
                zIndex: 0,
                pointerEvents: 'none',
                transition: 'height 0.3s ease-out'
              }}
            />
            
            {/* Línea de progreso azul continua */}
            <Box
              sx={{
                position: 'absolute',
                left: '20px',
                top: '20px',
                width: '2px',
                bgcolor: 'primary.main',
                zIndex: 1,
                pointerEvents: 'none',
                height: lineHeight > 0 ? `${lineHeight}px` : '0px',
                transition: 'height 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />

            {/* Steps */}
            {steps.map((step, index) => {
              const isActive = index <= activeStep;

              return (
                <Box
                  key={step.number}
                  ref={(el) => (stepRefs.current[index] = el)}
                  sx={{
                    display: 'flex',
                    gap: 3,
                    mb: { xs: 4, md: 6 },
                    position: 'relative',
                    '&:last-child': {
                      mb: 0
                    }
                  }}
                >
                  {/* Círculo con línea */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: '40px',
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: '3px solid',
                        borderColor: isActive ? 'primary.main' : 'grey.300',
                        bgcolor: isActive ? 'primary.main' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex: 10,
                        transition: 'all 0.4s ease-out',
                        boxShadow: '0 0 0 6px white'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: isActive ? 'white' : 'text.primary',
                          transition: 'color 0.4s ease-out'
                        }}
                      >
                        {step.number}
                      </Typography>
                    </Box>

                    {/* Conector horizontal hacia la derecha */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '19px',
                        left: '40px',
                        width: '24px',
                        height: '2px',
                        bgcolor: isActive ? 'primary.main' : '#e5e7eb',
                        pointerEvents: 'none',
                        transition: 'background-color 0.4s ease-out',
                        zIndex: 5
                      }}
                    />
                  </Box>

                  {/* Card a la derecha */}
                  <Box sx={{ flex: 1 }}>
                    <Card
                      elevation={0}
                      sx={{
                        border: '1px solid',
                        borderColor: isActive ? 'primary.main' : 'grey.200',
                        borderRadius: '14px',
                        transition: 'all 0.5s ease-out',
                        backgroundColor: 'white',
                        opacity: isActive ? 1 : 0.3,
                        transform: isActive ? 'translateX(0)' : 'translateX(20px)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Typography
                          variant="h5"
                          component="h3"
                          gutterBottom
                          sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            fontSize: '1.35rem'
                          }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            fontSize: '0.95rem',
                            lineHeight: 1.7
                          }}
                        >
                          {step.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorksSection;

